import {
  DatePicker as MuiDatePicker,
  FieldRef,
  PickerChangeHandlerContext,
  DateValidationError,
} from "@mui/x-date-pickers"

import { FocusEvent, FocusEventHandler, useEffect, useRef, useState } from "react"

import { DatePickerPropsType } from "@/types/components/datePicker"
import Icon from "@/components/icon/Icon"

import cx from "classnames"

import "./datePicker.scss"
import { YEAR_TO_DAY_FORMAT } from "@/constants/regex"
import { clampDateFromSections } from "./clampDateSections"

type PickerSlotProps = NonNullable<DatePickerPropsType["slotProps"]>

export default function DatePicker({
  className,
  view: propView,
  message,
  showMessage,
  onChange,
  onAccept,
  onFieldBlur,
  error,
  value,
  disabled,
  defaultValueNull,
  placeholder,
  ...props
}: DatePickerPropsType) {
  const [currentDate, setCurrentDate] = useState(value || (defaultValueNull ? null : new Date()))
  const [view, setView] = useState<"year" | "month" | "day">(propView || "day")
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef(null)
  const fieldRef = useRef<FieldRef<Date | null>>(null)

  const isInvalidDate = (date: Date | null): date is Date => !!date && Number.isNaN(date.getTime())

  /**
   * 존재하지 않는 날짜(5/31 에서 월만 2월로 바꾼 경우)를 그 달의 마지막 날로 맞춘다.
   * MUI 는 이런 입력을 Invalid Date 로 확정해 버리므로, 그대로 올리면 조회에 NaN 이 실려 나간다.
   * 섹션 값은 이 콜백 이후에 갱신되기 때문에, 화면에 반영된 뒤(rAF) 읽어서 보정한다.
   */
  const scheduleClamp = (context: PickerChangeHandlerContext<DateValidationError>) => {
    requestAnimationFrame(() => {
      const clamped = clampDateFromSections(fieldRef.current?.getSections() ?? [])

      if (!clamped) {
        return
      }

      setCurrentDate(clamped)
      onChange?.(clamped, context)
      onAccept?.(clamped, context)
    })
  }

  const onViewChanged = (newView: "year" | "month" | "day") => {
    setView(newView)
    props?.onViewChange?.(newView)
  }

  const onValueChange = (
    newDate: Date | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    if (isInvalidDate(newDate)) {
      // 잘못된 날짜를 그대로 올리면 조회에 NaN 이 실려 나간다. 보정 후 한 번만 올린다.
      setCurrentDate(newDate)
      scheduleClamp(context)

      return
    }

    if (newDate) {
      setCurrentDate(newDate)
    }

    if (view === "day") {
      onChange?.(newDate, context)
    }
  }

  const onValueAccept = (
    newDate: Date | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    // 보정 대상이면 scheduleClamp 가 보정된 값으로 대신 알린다
    if (isInvalidDate(newDate)) {
      return
    }

    onAccept?.(newDate, context)
  }

  useEffect(() => {
    setCurrentDate(value || (defaultValueNull ? null : new Date()))
  }, [value])

  // MUI 8 의 날짜 필드는 연/월/일 섹션이 각각 포커스를 갖는다.
  // 섹션을 옮길 때마다 focusout 이 올라오므로, 필드 밖으로 나간 경우만 걸러서 알린다.
  const onContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node)) {
      return
    }

    onFieldBlur?.(event)
  }

  return (
    <div className={cx("DatePickerCont", className, { error, disabled })} onBlur={onContainerBlur}>
      <MuiDatePicker
        {...props}
        views={["year", "month", "day"]}
        view={view}
        disabled={disabled}
        fixedWeekNumber={6}
        showDaysOutsideCurrentMonth
        value={currentDate}
        ref={ref}
        onChange={onValueChange}
        onAccept={onValueAccept}
        format={YEAR_TO_DAY_FORMAT}
        onViewChange={onViewChanged}
        slots={{
          openPickerIcon: CustomOpenPickerIcon,
        }}
        slotProps={{
          ...props.slotProps,
          // 섹션 값(연/월/일)을 읽어 존재하지 않는 날짜를 보정하기 위해 필드 ref 를 잡는다.
          // unstableFieldRef 는 field 슬롯의 prop 이지만 slotProps 타입에는 노출돼 있지 않다.
          field: {
            ...(props.slotProps?.field ?? {}),
            unstableFieldRef: fieldRef,
          } as PickerSlotProps["field"],
          layout: {
            className: "calendar-layout",
          },
          day: {
            className: "calendar-day",
          },
          monthButton: {
            className: "calendar-month-button",
          },
          yearButton: {
            className: "calendar-year-button",
          },
          popper: props.slotProps?.popper,
          textField: {
            ...(props.slotProps?.textField ?? {}),
            placeholder: placeholder,
            // 내부 포커스 표시용 핸들러가 호출자의 onFocus/onBlur 를 덮어쓰지 않도록 이어서 호출한다
            onFocus: (event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(true)
              ;(props.slotProps?.textField as { onFocus?: FocusEventHandler })?.onFocus?.(event)
            },
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(false)
              ;(props.slotProps?.textField as { onBlur?: FocusEventHandler })?.onBlur?.(event)
            },
            sx:
              !currentDate && !isFocused && !placeholder
                ? {
                    "& .MuiPickersInputBase-sectionsContainer span": {
                      color: "transparent !important",
                    },
                  }
                : undefined,
          },
        }}
      />
      {showMessage && <p className="message">{message}</p>}
    </div>
  )
}

const CustomOpenPickerIcon = () => <Icon type="calendar2" className="calendar-open-icon" />
